import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivityService } from 'app/services/activity.service';
import { AttrIndexService } from 'app/services/attr-index.service';
import { DomainService, GivColumnDomain } from 'app/services/domain.service';
import { EditSplitMergeService } from 'app/services/edit-split-merge.service';
import { EditToolService, NeutralColumn } from 'app/services/edit-tool.service';
import { LayerCategoryStandardSchema } from 'app/services/field-mapping.service';
import { FloatPopupService } from 'app/services/float-popup.service';
import { LayerService } from 'app/services/layer.service';
import { MainDataService } from 'app/services/main-data.service';
import { CenterlineSide, ResolutionCenterService } from 'app/services/resolution-center.service';
import { ParityValue, ResolutionQcService } from 'app/services/resolution-qc.service';
import WKT from 'ol/format/WKT';
import { LineString, MultiLineString } from 'ol/geom';
import { Subscription } from 'rxjs';

interface NeutralColumnExt extends NeutralColumn {
	standardLeft: LayerCategoryStandardSchema;
	standardRight: LayerCategoryStandardSchema;
}

@Component({
  selector: 'app-line-split',
  templateUrl: './line-split.component.html',
  styleUrls: ['./line-split.component.css']
})
export class LineSplitComponent implements OnInit, OnDestroy {

	record;
	splits: any[] = [];
	cols: NeutralColumnExt[];

	fromLeftColName: string;
	fromRightColName: string;
	toLeftColName: string;
	toRightColName: string;
	parityLeftColName: string;
	parityRightColName: string;

	globalIdCol: string;

	domainsByColumn: {
		[prop: string]: GivColumnDomain;
	} = {};

	private splitLineSub: Subscription;
	constructor(
		private activityService: ActivityService,
		private attrIndexService: AttrIndexService,
		private domainService: DomainService,
		private splitMergeService: EditSplitMergeService,
		private editToolService: EditToolService,
		private floatService: FloatPopupService,
		private layerService: LayerService,
		private mainDataService: MainDataService,
		private resoCenterService: ResolutionCenterService,
		private resoQcService: ResolutionQcService
	) { }

	cancel() {
		this.splitMergeService.splitLineBS.next({
			id: this.record[this.globalIdCol],
			isCancel: true
		});
		this.floatService.closePopup(LineSplitComponent);
	}
	apply() {
		this.floatService.closePopup(LineSplitComponent);
	}

	onEditAttr(splitIdx, colProp: string) {
		const split = this.splits[splitIdx];
		this.mainDataService.setDataAttrs(split[this.globalIdCol], colProp, split[colProp]);
	}
	jointChange(e: {side: CenterlineSide; idx}) {
		let direction: 'increase' | 'decrease';
		const side = e.side;
		const lineIdx = e.idx;

		let fromColName: string, toColName: string, parityColName: string;
		if (side === 'left') {
			fromColName = this.fromLeftColName;
			toColName = this.toLeftColName;
			parityColName = this.parityLeftColName;
		} else {
			fromColName = this.fromRightColName;
			toColName = this.toRightColName;
			parityColName = this.parityRightColName;
		}
		const firstSplit = this.splits[0];
		const secondSplit = this.splits[1];
		const firstCol = toColName;
		const secondCol = fromColName;
		if (lineIdx === 0) {
			direction = 'increase';
		} else if (lineIdx === 1) {
			direction = 'decrease';
		} else {
			return;
		}
		let firstValue: number = firstSplit[firstCol];
		const firstParity = firstSplit[parityColName];
		const firstDelta = this.resoCenterService.getDeltaValueByParity(firstValue, firstParity, direction);
		let secondValue: number = secondSplit[secondCol];
		const secondParity = secondSplit[parityColName];
		const secondDelta = this.resoCenterService.getDeltaValueByParity(secondValue, secondParity, direction);
		firstSplit[firstCol] = firstValue + firstDelta;
		secondSplit[secondCol] = secondValue + secondDelta;
	}

	split(splitPartsId) {
		const notExist = (colName) => {
			return !this.record[colName] && this.record[colName] !== 0;
		}
		if (notExist(this.fromLeftColName) || notExist(this.fromRightColName) || notExist(this.toLeftColName) || notExist(this.toRightColName)) {
			return;
		}
		const lengthList: number[] = [];
		splitPartsId.forEach(id => {
			const part = this.mainDataService.getDataAttrs(id);
			const wkt = part[this.attrIndexService.geomColManual];
			if (wkt) {
				const wktFormat = new WKT();
				const geom = (<LineString>wktFormat.readGeometry(wkt)); // TODO: multiline
				lengthList.push(geom.getLength());
			} else {
				lengthList.push(0);
			}
			this.splits.push(part);
		});
		const total = lengthList.reduce((prev, curr) => {
			return prev + curr;
		}, 0);
		const parityLeft = this.record[this.parityLeftColName];
		const parityRight = this.record[this.parityRightColName];
		const percentageList = lengthList.map(length => length / total);
		const leftRange = [this.record[this.fromLeftColName], this.record[this.toLeftColName]];
		const leftRangeSplits = this.splitRange(leftRange, percentageList, parityLeft);
		const rightRange = [this.record[this.fromRightColName], this.record[this.toRightColName]];
		const rightRangeSplits = this.splitRange(rightRange, percentageList, parityRight);
		this.splits.forEach((rec, idx) => {
			rec[this.fromLeftColName] = leftRangeSplits[idx][0];
			rec[this.toLeftColName] = leftRangeSplits[idx][1];
			rec[this.fromRightColName] = rightRangeSplits[idx][0];
			rec[this.toRightColName] = rightRangeSplits[idx][1];
			// Keep original parity
				// rec[this.parityLeftColName] = this.resoQcService.getParity(rec[this.fromLeftColName], rec[this.toLeftColName]);
				// rec[this.parityRightColName] = this.resoQcService.getParity(rec[this.fromRightColName], rec[this.toRightColName]);
			const id = rec[this.globalIdCol];
			this.mainDataService.setDataAttrs(id, this.fromLeftColName, rec[this.fromLeftColName]);
			this.mainDataService.setDataAttrs(id, this.toLeftColName, rec[this.toLeftColName]);
			this.mainDataService.setDataAttrs(id, this.fromRightColName, rec[this.fromRightColName]);
			this.mainDataService.setDataAttrs(id, this.toRightColName, rec[this.toRightColName]);
		});
	}

	private splitRange = (range: number[], percentageList: number[], parity?: ParityValue) => {
		const deltaRange = range[1] - range[0];
		const rangeList: number[][] = [];
		if (deltaRange === 0) {
			percentageList.forEach(i => rangeList.push([range[0], range[1]]));
		} else {
			let curr = range[0];
			percentageList.forEach(percent => {
				let increased = Number((deltaRange * percent).toFixed(0));
				let toMeas = curr + increased;
				if (parity === 'E' && toMeas % 2 === 1) {
					increased++;
				} else if (parity === 'O' && toMeas % 2 === 0) {
					increased++;
				}
				// else if (parity === 'B' && (curr + toMeas) % 2 === 0) {
				// 	increased++;
				// }
				toMeas = curr + increased;
	
				if (toMeas < curr) {
					toMeas = curr;
				}
				if (toMeas > range[1]) {
					toMeas = range[1];
				}
				const currRange = [curr, toMeas];
				
				let delta = 2;
				if (parity !== 'E' && parity !== 'O') {
					delta = 1;
				}
				curr += increased + delta;
				if (curr > range[1]) {
					curr = range[1];
				}
				rangeList.push(currRange);
			});
		}
		return rangeList;
	}
	ngOnInit() {
		this.fromLeftColName = this.attrIndexService.fromAddrLeftColName;
		this.fromRightColName = this.attrIndexService.fromAddrRightColName;
		this.toLeftColName = this.attrIndexService.toAddrLeftColName;
		this.toRightColName = this.attrIndexService.toAddrRightColName;
		this.parityLeftColName = this.attrIndexService.parityLeftColName;
		this.parityRightColName = this.attrIndexService.parityRightColName;

		this.globalIdCol = this.attrIndexService.rcUuidCol; // 'grd_' + this.attrIndexService.globalIdColStandard;

		const cols = this.editToolService.transformLeftRightColumnsToNeutral(this.mainDataService.currentColumns);
		const colsExt: NeutralColumnExt[] = [];
		// add properties
		const layer = this.layerService.layerListBS.getValue().layers.find(l => l.category.code === this.attrIndexService.cateCodeCenterline);
		const layerCate = layer.category;

		cols.forEach(c => {
			const prefix = layerCate.colPrefix + '_';
			const standardColNameLeft = c.leftProp.startsWith(prefix) ? c.leftProp.substring(4) : c.leftProp;
			const standardColNameRight = c.rightProp.startsWith(prefix) ? c.rightProp.substring(4) : c.rightProp;
			const standardLeft = layerCate.standardCols.find(s => s.colName.toLowerCase() === standardColNameLeft.toLowerCase());
			const standardRight = layerCate.standardCols.find(s => s.colName.toLowerCase() === standardColNameRight.toLowerCase());
			colsExt.push(Object.assign({
				standardLeft: standardLeft,
				standardRight: standardRight
			}, c));
		});
		this.cols = colsExt;

		this.splitLineSub = this.splitMergeService.splitLineBS.subscribe(data => {
			if (!data || data.isCancel) {
				return;
			}
			this.splits = [];
			const id = data.id;
			this.record = this.mainDataService.getDataAttrs(id);
			this.split(data.splitPartsId);

			// Get domains
			const layerCate = this.activityService.selectedLayerBS.getValue().layer.category;
			const cols: {
				prop; standard;
			}[] = [];
			this.cols.forEach(c => {
				// const prefix = layerCate.colPrefix + '_';
				// const standardColNameLeft = c.leftProp.startsWith(prefix) ? c.leftProp.substring(4) : c.leftProp;
				// const standardColNameRight = c.rightProp.startsWith(prefix) ? c.rightProp.substring(4) : c.rightProp;
				cols.push({
					prop: c.leftProp,
					standard: c.standardLeft // layerCate.standardCols.find(s => s.colName.toLowerCase() === standardColNameLeft.toLowerCase())
				});
				cols.push({
					prop: c.rightProp,
					standard: c.standardRight // layerCate.standardCols.find(s => s.colName.toLowerCase() === standardColNameRight.toLowerCase())
				});
			});
			this.domainsByColumn = this.domainService.getColumnDomains(cols, layerCate.code, this.record);
		});
	}
	ngOnDestroy(): void {
		this.splitLineSub.unsubscribe();
	}
}
